import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { CrossReferenceRejectionCategory } from '../dto/cross-reference-review.dto';
import type { ExternalArticleNumberKind } from '../dto/product-matching.dto';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CrossReferenceLineMatchEntity } from './cross-reference-line-match.entity';
import { CrossReferenceRequestLineEntity } from './cross-reference-request-line.entity';
import { ItemEntity } from './item.entity';

/**
 * Which way a reviewer's judgement pointed.
 *
 * Only EXPLICIT judgements are recorded. A candidate closed out by a cascade
 * (`sibling_confirm`, `superseded`, `no_match`) is not one: confirming a
 * candidate must not teach the engine that its auto-closed rivals were wrong,
 * because nobody said they were.
 */
export type CrossReferenceMatchFeedbackPolarity = 'confirm' | 'reject';

/**
 * One reviewer judgement about an (external identifier → item) pair — the
 * GRADED half of the learning system.
 *
 * `item_cross_reference` records the equivalences confirmations establish, and
 * a single row there is a fact the engine recalls outright. This table is the
 * opposite kind of record: an append-only stream of events that the matcher
 * aggregates into a small, capped score adjustment. That difference is
 * deliberate — one click should never be able to make or break a pair, and a
 * reviewer who changes their mind should be able to take the signal back.
 *
 * Aggregation is GLOBAL across clients. The pair identity is a claim about what
 * the product IS ("this number does not mean that article"), which does not
 * become a different claim because a different customer asked. `fkClientId` is
 * carried for audit only, so a reviewer can still see whose request produced a
 * judgement; nothing reads it when the aggregate is computed.
 *
 * Reversal is a HARD delete (`repo.remove`), not a soft one. Every other row in
 * this domain is evidence of a decision that stands; these rows are the
 * decision's ongoing EFFECT on scoring, and undoing the decision means that
 * effect no longer exists. A soft-deleted event would have to be filtered out
 * of every aggregate forever, which is a rule waiting to be forgotten.
 */
@Index('idx_crmf_pair', ['identifierNormalized', 'identifierKind', 'fkItemId'])
// Undo reverses by the match row it came from, so that lookup gets its own key.
@Index('idx_crmf_match', ['fkCrossReferenceLineMatchId'])
@Entity('cross_reference_match_feedback')
export class CrossReferenceMatchFeedbackEntity extends BaseEntity {
	/** The external identifier exactly as it appeared on the request line. */
	@Column('varchar', { name: 'identifierValue', length: 128 })
	public identifierValue!: string;

	/**
	 * The comparison key, folded with the SAME `normalizeIdentifier` the engine
	 * and `item_cross_reference` use. One fold definition, never two.
	 */
	@Column('varchar', { name: 'identifierNormalized', length: 128 })
	public identifierNormalized!: string;

	@Column('varchar', { name: 'identifierKind', length: 16 })
	public identifierKind!: ExternalArticleNumberKind;

	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('varchar', { name: 'polarity', length: 16 })
	public polarity!: CrossReferenceMatchFeedbackPolarity;

	/** Null on a confirmation — only a rejection carries a stated reason. */
	@Column('varchar', { name: 'rejectionCategory', nullable: true, length: 32 })
	public rejectionCategory!: CrossReferenceRejectionCategory | null;

	/**
	 * Whose request produced this judgement. AUDIT ONLY — the aggregate the
	 * matcher reads is global, so nothing scores differently because of it.
	 */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	/** The candidate row the judgement was made on; undo reverses by it. */
	@Column('int', {
		name: 'fk_cross_reference_line_match__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceLineMatchId!: number | null;

	/** The request line the judgement was made on, for provenance. */
	@Column('int', {
		name: 'fk_cross_reference_request_line__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceRequestLineId!: number | null;

	@Column('int', { name: 'userId', nullable: true, unsigned: true })
	public userId!: number | null;

	@Column('datetime', { name: 'recordedAt', nullable: true })
	public recordedAt!: Date | null;

	@ManyToOne(() => ItemEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public fkItem!: ItemEntity;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity | null;

	// CASCADE on both request-side pointers: an event is a judgement ABOUT a
	// candidate on a line, so it has no meaning once that candidate is gone.
	@ManyToOne(() => CrossReferenceLineMatchEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_line_match__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceLineMatch!: CrossReferenceLineMatchEntity | null;

	@ManyToOne(() => CrossReferenceRequestLineEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_request_line__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceRequestLine!: CrossReferenceRequestLineEntity | null;
}
